import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useLoad, errMsg } from "@/lib/api";
import { PageHead, card, inputCls } from "@/components/admin/kit";

function useHear() {
  const api = useApi();
  const [busy, setBusy] = useState("");
  const hear = async (id, text) => {
    setBusy(id);
    try {
      const r = await api.post("/voice/test", { text, language: "te-IN" }, { responseType: "blob" });
      new Audio(URL.createObjectURL(r.data)).play();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return [busy, hear];
}

function AddRow({ onSaved, busy, hear }) {
  const api = useApi();
  const [f, setF] = useState({ written: "", spoken: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/pronunciations", f); toast.success(<span className="font-te">Saved — “{f.written}” will be said as “{f.spoken}”</span>); setF({ written: "", spoken: "" }); onSaved(); }
    catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <form onSubmit={save} className="grid gap-3 md:grid-cols-[1fr_1fr_auto_auto]" data-testid="pron-add-form">
      <input className={`${inputCls} font-te`} placeholder="Name as written in scripts (e.g. అన్విత)" value={f.written} onChange={(e) => setF({ ...f, written: e.target.value })} maxLength={80} data-testid="pron-written-input" />
      <input className={`${inputCls} font-te`} placeholder="How to say it (e.g. అన్-వి-తా)" value={f.spoken} onChange={(e) => setF({ ...f, spoken: e.target.value })} maxLength={120} data-testid="pron-spoken-input" />
      <Button type="button" variant="outline" disabled={!f.spoken.trim() || !!busy} onClick={() => hear("new", f.spoken)} data-testid="pron-try-btn">
        {busy === "new" ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Try it
      </Button>
      <Button type="submit" disabled={!f.written.trim() || !f.spoken.trim()} data-testid="pron-save-btn"><Plus size={16} /> Save</Button>
    </form>
  );
}

export default function Pronunciations() {
  const api = useApi();
  const [list, reload] = useLoad("/pronunciations");
  const [busy, hear] = useHear();
  const del = async (p) => {
    try { await api.delete(`/pronunciations/${p.id}`); toast.success("Removed"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="pronunciations-page">
      <PageHead title="Pronunciation list" sub="Tell the voice how to say tricky names. Scripts keep the normal spelling; only the recording uses the spoken form. Applies to every new recording — call scripts, phone checks and website clips." />
      <div className={`${card} grid gap-5`}>
        <AddRow onSaved={reload} busy={busy} hear={hear} />
        <p className="text-xs text-muted-foreground">Call scripts that use a changed name show “Voice out of date” and are re-recorded automatically before the next call. Website clips keep their approved recording until you press Listen and Save again.</p>
        <div className="grid gap-2" data-testid="pron-list">
          {(list || []).length === 0 && <p className="text-sm text-muted-foreground" data-testid="pron-empty">No names yet.</p>}
          {(list || []).map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-2xl bg-[var(--lav)] px-4 py-3" data-testid={`pron-row-${p.id}`}>
              <span className="font-te font-bold" data-testid="pron-row-written">{p.written}</span>
              <span className="text-muted-foreground">→ said as</span>
              <span className="font-te" data-testid="pron-row-spoken">{p.spoken}</span>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => hear(p.id, p.written)} data-testid={`pron-hear-${p.id}`}>
                  {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Hear it
                </Button>
                <Button size="sm" variant="outline" onClick={() => del(p)} aria-label="Remove" data-testid={`pron-delete-${p.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
